import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useFeature } from '../../../hooks/useAccess';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const toCarrier = c => ({
  id: c._id || c.id,
  name: c.name,
  code: c.code || '',
  products: c.products || [],
  activePolicies: c.activePolicies || 0,
  monthlyPremium: c.monthlyPremium || 0,
  status: c.status || 'active',
  contact: c.contact,
  email: c.email,
  phone: c.phone,
  address: c.address,
  city: c.city,
  canton: c.canton,
  bordereau: c.bordereau || 'Pending',
  bordereauHistory: c.bordereauHistory || [],
  bordereauxConfig: c.bordereauxConfig || { frequency: 'monthly', format: 'xlsx', dayOfMonth: 5, autoSend: false, recipients: [] },
  active: c.active !== false,
  createdAt: c.createdAt,
});

// ─── Period picker helper ─────────────────────────────────
function defaultPeriod() {
  const now = new Date();
  const to = new Date(now.getFullYear(), now.getMonth(), 0); // last day prev month
  const from = new Date(to.getFullYear(), to.getMonth(), 1);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

const useCarrierDetailFunctions = ({ initCarrier, onUpdate }) => {
  const { t } = useTranslation();
  const [carrier, setCarrier] = useState(initCarrier);
  const [activeTab, setActiveTab] = useState('overview');
  const bordereauxEnabled = useFeature('bordereaux');
  // The tenant config resolves after first paint, so a tab can disappear from
  // under the user. Fall back rather than render a tab body with no tab.
  useEffect(() => {
    if (!bordereauxEnabled && (activeTab === 'bordereaux' || activeTab === 'config')) {
      setActiveTab('overview');
    }
  }, [bordereauxEnabled, activeTab]);
  const [bordereaux, setBordereaux] = useState([]);
  const [loadingB, setLoadingB] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genForm, setGenForm] = useState({ type: 'claims', ...defaultPeriod() });
  const [showGenForm, setShowGenForm] = useState(false);
  const [config, setConfig] = useState(carrier.bordereauxConfig);
  const [savingCfg, setSavingCfg] = useState(false);
  const [cfgSaved, setCfgSaved] = useState(false);
  const [sending, setSending] = useState(null);

  const fetchBordereaux = useCallback(async () => {
    setLoadingB(true);
    try {
      const resp = await axios.get(`${BASE_URL}/bordereaux`, {
        params: { carrierId: carrier.id, limit: 50 },
        withCredentials: true,
      });
      if (resp.data?.data) setBordereaux(resp.data.data);
    } catch {
      /* use empty */
    } finally {
      setLoadingB(false);
    }
  }, [carrier.id]);

  useEffect(() => {
    if (bordereauxEnabled && activeTab === 'bordereaux') fetchBordereaux();
  }, [activeTab, bordereauxEnabled, fetchBordereaux]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(
        `${BASE_URL}/bordereaux/generate`,
        {
          carrierId: carrier.id,
          type: genForm.type,
          periodFrom: genForm.from,
          periodTo: genForm.to,
        },
        { withCredentials: true },
      );
      setShowGenForm(false);
      // Poll for completion after 3s
      setTimeout(() => fetchBordereaux(), 3000);
    } catch {
      /* ignore */
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (id, format) => {
    window.open(`${BASE_URL}/bordereaux/${id}/download`, '_blank');
  };

  const handleSend = async id => {
    setSending(id);
    try {
      await axios.post(`${BASE_URL}/bordereaux/${id}/send`, {}, { withCredentials: true });
      setBordereaux(prev => prev.map(b => (b._id === id ? { ...b, status: 'sent' } : b)));
    } catch {
      /* silent */
    } finally {
      setSending(null);
    }
  };

  const handleSaveConfig = async () => {
    setSavingCfg(true);
    try {
      const resp = await axios.patch(`${BASE_URL}/risk-carriers/${carrier.id}`, { bordereauxConfig: config }, { withCredentials: true });
      if (resp.data?.data) {
        const updated = toCarrier(resp.data.data);
        setCarrier(updated);
        onUpdate(updated);
      }
      setCfgSaved(true);
      setTimeout(() => setCfgSaved(false), 2500);
    } catch {
      /* silent */
    } finally {
      setSavingCfg(false);
    }
  };

  // Bordereaux tabs are gated on the tenant's own feature flag, read through
  // the same useFeature hook that gates the nav and the routes.
  //
  // Module-level gating already hid Bordereaux from the sidebar, but the
  // Provider detail page carried its own tab strip and kept offering
  // "Bordereaux" and "Generate New Bordereaux" on a tenant with the feature
  // off. The backend refuses the call, so nothing could be generated — which
  // makes it worse, not better: the user is shown a capability, clicks it, and
  // is told no. An unavailable feature should be absent, not disabled and not
  // an empty card advertising itself.
  //
  // The Configuration tab is "Bordereaux Configuration" and goes with it.
  const TABS = [
    { id: 'overview',   label: t('screens.riskCarriers.tabOverview',  { defaultValue: 'Overview' }) },
    ...(bordereauxEnabled ? [
      { id: 'bordereaux', label: t('screens.riskCarriers.tabBordereaux',{ defaultValue: 'Bordereaux' }) },
      { id: 'config',     label: t('screens.riskCarriers.tabConfig',    { defaultValue: 'Configuration' }) },
    ] : []),
  ];

  return {
    carrier,
    setCarrier,
    activeTab,
    setActiveTab,
    bordereaux,
    setBordereaux,
    loadingB,
    setLoadingB,
    generating,
    setGenerating,
    genForm,
    setGenForm,
    showGenForm,
    setShowGenForm,
    config,
    setConfig,
    savingCfg,
    setSavingCfg,
    cfgSaved,
    setCfgSaved,
    sending,
    setSending,
    fetchBordereaux,
    handleGenerate,
    handleDownload,
    handleSend,
    handleSaveConfig,
    TABS,
  };
};

export default useCarrierDetailFunctions;
